import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Col, Dropdown, Modal, Row, Table as BTTable } from "react-bootstrap";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import CreatableSelect from "react-select/creatable";
import { BootstrapLayout } from "../../components/BootstrapLayout";
import { InventoryItemsContext } from "../../components/InventoryItemsContext";
import { SmallDangerButton } from "../../components/layout/Button";
import { UploadField } from "../../components/uploadField/UploadField";
import { useFormConfig } from "../../hooks/useFormConfig";
import { getInventoryItemsAJAX, getInventoryItemsForFormAJAX } from "../../actions/inventory_items";
import { stripTags, template, translucentColor } from "../../utils";
import {
    alignRow,
    alignTableRows,
    cellHashCode,
    hydrateAlignedRows,
    toAlignedColumns,
} from "../../utilities/tableRowAligner";
import { formatMonthYear } from "../../utilities/monthYear";
import {
    applyMultiRowEdit,
    isBulkLockedColumn,
    isPerRowColumn,
    PerRowEdits,
    stripTransientColumnData,
} from "../../utilities/tableMultiRowEdit";
import { calculateTotalWorkedHours } from "../../utilities/workHours";
import { DueBadge } from "../inputFields/DueBadge";
import { MonthYearInput } from "../inputFields/MonthYearInput";
import { SummaryTableRow } from "./SummaryTableRow";

const colorStyles = {
    option: (styles, state) => {
        return {
            ...styles,
            ":hover": {
                color: translucentColor(state.data.cell?.color || "#5fc1cd", 0.9),
                backgroundColor: translucentColor(state.data.cell?.color || "#5fc1cd", 0.1),
            },
        };
    },
    multiValue: (styles, state) => {
        return {
            ...styles,
            color: translucentColor(state.data.cell?.color || "#5fc1cd", 0.9),
            backgroundColor: translucentColor(state.data.cell?.color || "#5fc1cd", 0.1),
        };
    },
};

const FORMS_WITH_SUMMARY = ["00_Leistungsnachweis", "000_Rechnung", "000_Angebot"];

// Keeps the row action buttons reachable on long tables (e.g. Türauflistung):
// the bar pins to the bottom of the scroll viewport while the table is in view.
const StickyTableActions = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 50;
    background: rgba(255, 255, 255, 0.95);
    border-top: 1px solid #ddd;
    padding: 10px 0;
    margin-top: 4px;

    @media (pointer: coarse) {
        padding: 24px 0 40px;
    }

    @media print {
        position: static;
        background: transparent;
        border-top: none;
        padding: 0;
    }
`;

const TableActions: any = ({
    activeRows,
    addRow,
    editRow,
    removeRow,
    addRowAbove,
    addRowBelow,
    copyRows,
    selectAllRows,
    readonly = false,
}) => {
    return (
        <Dropdown as={ButtonGroup}>
            {!readonly && (
                <Button
                    variant={"primary"}
                    style={{ borderColor: "#34aebd", backgroundColor: "#5fc1cd" }}
                    onClick={selectAllRows}
                >
                    Alle Zeilen markieren
                </Button>
            )}

            {activeRows.length <= 1 && !readonly && (
                <Button
                    variant={"primary"}
                    style={{ borderColor: "#34aebd", backgroundColor: "#5fc1cd" }}
                    onClick={addRow}
                >
                    Zeile hinzufügen
                </Button>
            )}

            {activeRows.length === 1 && (
                <>
                    <Dropdown.Toggle
                        split
                        variant={"primary"}
                        style={{
                            marginRight: "10px",
                            borderColor: "#34aebd",
                            backgroundColor: "#5fc1cd",
                        }}
                        id="dropdown-split-basic"
                    />

                    <Dropdown.Menu>
                        <Dropdown.Item onClick={addRowAbove}>
                            Über aktiver Zeile einfügen
                        </Dropdown.Item>
                        <Dropdown.Item onClick={addRowBelow}>
                            Unter aktiver Zeile einfügen
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </>
            )}

            {activeRows.length > 0 && (
                <>
                    <Button
                        style={{
                            borderColor: "#34aebd",
                            backgroundColor: "#5fc1cd",
                        }}
                        onClick={editRow}
                    >
                        {activeRows.length} {activeRows.length === 1 ? "Zeile" : "Zeilen"}{" "}
                        bearbeiten
                    </Button>

                    <Button
                        style={{
                            borderColor: "#34aebd",
                            backgroundColor: "#5fc1cd",
                        }}
                        onClick={copyRows}
                    >
                        {activeRows.length} {activeRows.length === 1 ? "Zeile" : "Zeilen"}{" "}
                        duplizieren
                    </Button>
                </>
            )}
            {activeRows.length > 0 && (
                <Button
                    style={{ borderColor: "#34aebd", backgroundColor: "#5fc1cd" }}
                    onClick={() => {
                        if (
                            !confirm(
                                `Sind Sie sicher dass ${
                                    activeRows.length === 0 ? "Zeile" : "Zeilen"
                                } Zeile entfernt werden soll?`
                            )
                        ) {
                            return;
                        }
                        removeRow();
                    }}
                >
                    {activeRows.length} {activeRows.length === 1 ? "Zeile" : "Zeilen"} löschen
                </Button>
            )}
        </Dropdown>
    );
};

const TableColumnValue = ({ column, index, hideMangelLabel = false }) => {
    if (!column) {
        return <></>;
    }

    if (column.type === "imageUpload") {
        const uploads = (column.value || []).filter((v) => {
            return v.file != null || v.id != null || v.upload_id != null;
        });
        return (
            <div key={`image-upload-${index}`}>
                <i className="fa fa-file"></i> {uploads.length}
            </div>
        );
    }

    if (column.type === "index") {
        return (
            <span key={`table-colum-value-${index}`} style={{ wordBreak: "keep-all" }}>
                {index + 1}
            </span>
        );
    }

    if (column.type === "autocomplete") {
        if (column.value && !Array.isArray(column.value)) {
            column.value = [column.value];
        }
        return (
            <div key={`table-colum-value-${index}`}>
                {column.value &&
                    column.value.map((val, index2) => {
                        return (
                            <React.Fragment key={`table-colum-value-${index2}`}>
                                <span style={{ color: val.cell ? val.cell.color : "#222" }}>
                                    {val.label}
                                </span>
                                {index2 < column.value.length - 1 ? (
                                    <span>
                                        ,<br />
                                    </span>
                                ) : (
                                    ""
                                )}
                            </React.Fragment>
                        );
                    })}
                {column.notes && typeof column.notes === "string" && column.notes.trim() && (
                    <div style={{ marginTop: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {!hideMangelLabel && <strong>Beschreibung Mängel:</strong>} {column.notes}
                    </div>
                )}
            </div>
        );
    }

    if (column.type === "date" && column.monthYearOnly && typeof column.value === "string") {
        return (
            <span key={`table-colum-value-${index}`}>
                {formatMonthYear(column.value) || column.value}
                <DueBadge value={column.value} config={column.dueInventoryItem} />
            </span>
        );
    }

    return (
        <span key={`table-colum-value-${index}`}>
            {typeof column.value === "string" ? column.value : <></>}
            {column.type === "date" && (
                <DueBadge value={column.value} config={column.dueInventoryItem} />
            )}
        </span>
    );
};

const buildDisplayRow = (row, columns) => {
    const safeColumns = Array.isArray(columns) ? columns : [];

    return alignRow(row, toAlignedColumns(safeColumns)).map((cell, index) =>
        cell === null
            ? {
                  ...safeColumns[index],
                  value: undefined,
                  field: null,
              }
            : safeColumns[index] && safeColumns[index].type === "date" && cell.type === "date"
            ? {
                  ...cell,
                  monthYearOnly: !!safeColumns[index].monthYearOnly,
                  dueInventoryItem: safeColumns[index].dueInventoryItem,
              }
            : cell
    );
};

const buildPersistedRow = (row: any[], columns: any[]) => {
    return buildDisplayRow(row, columns).map((cell, index) => {
        const columnHashCode = cellHashCode(columns?.[index]);
        const persisted: any = {
            ...stripTransientColumnData(cell),
            field: null,
        };
        if (columnHashCode) {
            persisted.hashCodeRef = columnHashCode;
        }
        return persisted;
    });
};

const isReparaturTitle = (title: any) => {
    return (
        stripTags(title || "")
            .trim()
            .toLowerCase() === "reparatur"
    );
};

const TablePresenter = ({
    activeRows,
    columns,
    data,
    onRowClick,
    showSummary,
    hideMangelLabel,
}) => {
    const totalHours = calculateTotalWorkedHours(data);

    console.log("COLS", columns);
    console.log("data", data);

    const columnsToKeep = columns
        .map((c: any, idx: number) => (!isReparaturTitle(c?.title) ? idx : -1))
        .filter((idx) => idx !== -1);

    //NOTE Structure of Türliste is going to change so we need to do this to
    //hide legacy data because old forms are getting recyled each year.

    // Filter columns by index
    const filteredColumns = columnsToKeep.map((idx) => columns[idx]);

    // Filter data by index for each row
    const normalizedData = data.map((row: any[]) => buildDisplayRow(row, columns));
    const filteredData = normalizedData.map((row: any[]) => columnsToKeep.map((idx) => row[idx]));

    const tableBody = useMemo(() => {
        const rows = filteredData.map((dataRow, index) => {
            return (
                <tr
                    onClick={() => onRowClick(index)}
                    className={activeRows.includes(index) ? "tr tr-active" : "tr"}
                    key={index}
                >
                    {dataRow.map((dataColumn, columnIndex) => {
                        return (
                            <td
                                key={columnIndex}
                                style={
                                    dataColumn?.title === "Baujahr"
                                        ? { whiteSpace: "nowrap" }
                                        : undefined
                                } // https://trello.com/c/AS0rfb2w
                            >
                                <TableColumnValue
                                    column={dataColumn}
                                    key={`ci-${columnIndex}`}
                                    index={index}
                                    hideMangelLabel={hideMangelLabel}
                                />
                            </td>
                        );
                    })}
                </tr>
            );
        });

        if (showSummary && data.length > 0 && totalHours > 0) {
            rows.push(<SummaryTableRow data={data} columns={filteredColumns} />);
        }

        return rows;
    }, [data, activeRows, columns, totalHours, showSummary, hideMangelLabel]);

    return (
        <BTTable responsive>
            <thead>
                <tr>
                    {filteredColumns.map((col, index) => {
                        return <th key={index}>{stripTags(col?.title)}</th>;
                    })}
                </tr>
            </thead>
            <tbody>{tableBody}</tbody>
        </BTTable>
    );
};

const useColumnInventoryItems = (enabled: boolean, formId: any, injected: any) => {
    const [loadedItems, setLoadedItems] = useState([]);

    useEffect(() => {
        // Offline-first: if the parent (app) injected items from local SQLite, trust them
        // even if empty. Web never passes this prop, so it falls through to the API path.
        if (!enabled || Array.isArray(injected)) {
            return undefined;
        }

        let isActive = true;
        const loadItems = formId
            ? getInventoryItemsForFormAJAX({}, formId)
            : getInventoryItemsAJAX({ page_size: -1 });

        loadItems
            .then((result) => {
                if (isActive) {
                    setLoadedItems(
                        result.data?.inventory_items || result.data?.inventoryItems || []
                    );
                }
            })
            .catch((err) => console.error(err));

        return () => {
            isActive = false;
        };
    }, [enabled, formId, injected]);

    return Array.isArray(injected) ? injected : loadedItems;
};

const TableColumnEditor: any = ({
    column,
    totalIndex,
    onChange,
    index,
    rowIndex,
    table,
    designer,
    inventoryItems: injectedInventoryItems,
    onCreateInventoryItem,
}) => {
    const [uploads, setUploads] = useState(Array.isArray(column.value) ? column.value : []);
    const [createdInventoryItems, setCreatedInventoryItems] = useState([]);
    const [proposal, setProposal] = useState<null | { shortName: string }>(null);
    const [isProposing, setIsProposing] = useState(false);
    const contextInventoryItems = useContext(InventoryItemsContext);
    const availableInventoryItems = contextInventoryItems ?? injectedInventoryItems;
    const { entities } = useSelector<any, any>((state) => state);
    const { entity_id, task_id }: any = useParams() as any;
    const task = useSelector((state: any) => {
        return state.tasks?.tasks.find(({ id }) => {
            return String(id) === task_id;
        });
    });
    const entity = entities?.entities.find((entity: any) => {
        return entity_id === String(entity.id);
    });

    const form = useSelector<any, any>((state) => {
        return state.forms?.forms.find((f) => f.id === task?.form_id);
    });

    const designerForm = useSelector<any, any>((state) => {
        return state.forms?.form || state.forms?.current || state.forms?.active;
    });

    const formData =
        typeof form?.form_data === "string" ? JSON.parse(form.form_data) : form?.form_data || {};
    const config = useFormConfig(formData.fields);

    const inventoryItems = useColumnInventoryItems(
        isPerRowColumn(column),
        column?.form?.id || form?.id || designerForm?.id || designer?.form?.id,
        availableInventoryItems
    );

    useEffect(() => {
        if (column.type === "imageUpload") {
            onChange({ column, index, value: uploads });
        }
    }, [uploads]);

    const clearField = () => {
        onChange({ column, index, value: null });
    };

    const mergedInventoryItems = () => {
        const merged = [...inventoryItems];
        createdInventoryItems.forEach((item: any) => {
            const exists = merged.some(
                (known: any) => (known?.short_name || known) === item.short_name
            );
            if (!exists) {
                merged.push(item);
            }
        });
        return merged;
    };

    const selectValues = () => {
        const sourceValues =
            column?.field?.name === "inventory_item_select"
                ? mergedInventoryItems()
                : column.source || [];

        return sourceValues.map((cellVal) => {
            return {
                label:
                    cellVal?.short_name ||
                    cellVal?.name ||
                    cellVal?.label ||
                    cellVal?.value ||
                    cellVal,
                value:
                    cellVal?.short_name ||
                    cellVal?.name ||
                    cellVal?.label ||
                    cellVal?.value ||
                    cellVal,
                cell: cellVal,
            };
        });
    };

    const defaultValueForSelect = () => {
        if (column.field?.rawOptions?.autofill && (column.source || inventoryItems.length > 0)) {
            const refValue = template(column.field.rawOptions.autofill, {}, column.formName);
            const defValue = selectValues().find(
                (c) => c.label === refValue || c.value === refValue
            );
            return [defValue];
        }
        return column.value;
    };

    const [selectedInventory, setSelectedInventory] = useState(() => {
        const initial = defaultValueForSelect();
        return Array.isArray(initial) ? initial.filter(Boolean) : [];
    });
    const canCreateInventoryItem =
        column?.field?.name === "inventory_item_select" &&
        typeof onCreateInventoryItem === "function";

    const inventoryOptionLabel = (option: any) =>
        option?.cell?.approved === false ? `${option.label} (unter Vorbehalt)` : option.label;

    const isValidNewInventoryOption = (inputValue: string) => {
        if (!canCreateInventoryItem) {
            return false;
        }
        const needle = (inputValue || "").trim().toLowerCase();
        if (!needle) {
            return false;
        }
        return !selectValues().some((option) => String(option.value).toLowerCase() === needle);
    };

    const handleCreateInventoryItem = (inputValue: string) => {
        const shortName = (inputValue || "").trim();
        if (!shortName) {
            return;
        }
        setProposal({ shortName });
    };

    const submitProposal = async () => {
        const shortName = (proposal?.shortName || "").trim();
        if (!shortName || isProposing) {
            return;
        }
        setIsProposing(true);
        let item: any = null;
        try {
            item = await onCreateInventoryItem({
                short_name: shortName,
                prompt: false,
                form: column?.form || form,
            });
        } catch (e) {
            item = null;
        }
        setIsProposing(false);
        if (!item) {
            return;
        }
        setProposal(null);
        setCreatedInventoryItems((previous) => [...previous, item]);
        const option = { label: item.short_name, value: item.short_name, cell: item };
        const next = [...(Array.isArray(selectedInventory) ? selectedInventory : []), option];
        setSelectedInventory(next);
        onChange({ column, index, value: next });
    };

    return (
        <div key={`col-${index}`}>
            {column.type === "index" && <></>}

            {column.type === "date" && column.monthYearOnly && (
                <div style={{ display: "flex" }}>
                    <MonthYearInput
                        value={typeof column.value === "string" ? column.value : ""}
                        onChange={(stored) => onChange({ column, index, value: stored || null })}
                    />
                    {column.value && (
                        <SmallDangerButton
                            style={{
                                marginLeft: "15px",
                                display: "inline-block",
                            }}
                            onClick={clearField}
                        >
                            X
                        </SmallDangerButton>
                    )}
                </div>
            )}

            {column.type === "date" && !column.monthYearOnly && (
                <div style={{ display: "flex" }}>
                    <input
                        type="date"
                        placeholder={""}
                        onChange={(v) =>
                            onChange({
                                column,
                                index,
                                value: v.currentTarget.value,
                            })
                        }
                        className="form-control"
                        defaultValue={column.value}
                    />
                    {column.value && (
                        <SmallDangerButton
                            style={{
                                marginLeft: "15px",
                                display: "inline-block",
                            }}
                            onClick={clearField}
                        >
                            X
                        </SmallDangerButton>
                    )}
                </div>
            )}

            {column.type === "date" && (
                <DueBadge value={column.value} config={column.dueInventoryItem} />
            )}

            {column.type === "time" && (
                <input
                    type="time"
                    placeholder={""}
                    onChange={(v) =>
                        onChange({
                            column,
                            index,
                            value: v.currentTarget.value,
                        })
                    }
                    className="form-control"
                    defaultValue={column.value}
                />
            )}

            {column.type === "autocomplete" && (
                <div key={`createable-select-${index}`}>
                    {column.field?.name === "inventory_item_select" && (
                        <CreatableSelect
                            closeMenuOnSelect={false}
                            isMulti
                            isClearable
                            value={selectedInventory}
                            styles={colorStyles}
                            menuPlacement={"auto"}
                            menuShouldScrollIntoView={true}
                            classNamePrefix="rps-combo"
                            isValidNewOption={isValidNewInventoryOption}
                            onCreateOption={handleCreateInventoryItem}
                            formatCreateLabel={(v: string) =>
                                `„${v}" als neue Position anlegen (unter Vorbehalt)`
                            }
                            formatOptionLabel={inventoryOptionLabel}
                            onChange={(v) => {
                                setSelectedInventory(Array.isArray(v) ? v : []);
                                onChange({ column, index, value: v });
                            }}
                            options={selectValues().filter((r) => typeof r.value !== "object")}
                        />
                    )}
                    {column.field?.name !== "inventory_item_select" && (
                        <CreatableSelect
                            closeMenuOnSelect={false}
                            isMulti
                            isClearable
                            defaultValue={defaultValueForSelect()}
                            styles={colorStyles}
                            menuPlacement={"auto"}
                            menuShouldScrollIntoView={true}
                            classNamePrefix="rps-combo"
                            onChange={(v) => {
                                onChange({ column, index, value: v });
                            }}
                            options={selectValues().filter((r) => typeof r.value !== "object")}
                        />
                    )}
                    {column.field?.name === "inventory_item_select" && proposal && (
                        <div
                            style={{
                                marginTop: 12,
                                padding: 12,
                                border: "1px solid #ced4da",
                                borderRadius: 4,
                            }}
                        >
                            <strong style={{ display: "block" }}>Neue LV-Position</strong>
                            <p style={{ fontSize: 12, margin: "4px 0 8px" }}>
                                Wird nach der Synchronisierung im Büro geprüft und freigegeben
                                (unter Vorbehalt).
                            </p>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Kurzname"
                                value={proposal.shortName}
                                onChange={(ev) => {
                                    const shortName = ev.currentTarget.value;
                                    setProposal((previous) =>
                                        previous ? { ...previous, shortName } : previous
                                    );
                                }}
                            />
                            <div style={{ marginTop: 8 }}>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={isProposing || !proposal.shortName.trim()}
                                    onClick={submitProposal}
                                >
                                    Anlegen
                                </Button>{" "}
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    disabled={isProposing}
                                    onClick={() => setProposal(null)}
                                >
                                    Abbrechen
                                </Button>
                            </div>
                        </div>
                    )}

                    {column.field?.name === "inventory_item_select" && (
                        <div style={{ marginTop: 12 }}>
                            <label style={{ display: "block", fontSize: 13, color: "#222" }}>
                                Beschreibung Mängel
                            </label>
                            <textarea
                                name={`${column.field.hashCode}__notes`}
                                defaultValue={column.notes || ""}
                                onChange={(ev) => {
                                    onChange({
                                        column,
                                        index,
                                        value: column.value,
                                        notes: ev.currentTarget.value,
                                    });
                                }}
                                className="form-control"
                                rows={3}
                            />
                        </div>
                    )}
                </div>
            )}

            {column.type === "imageUpload" && (
                <>
                    <UploadField
                        setUploads={setUploads}
                        namePattern={column.namePattern}
                        layout={BootstrapLayout}
                        subfields={[]}
                        uploads={uploads}
                        modal={false}
                        autoSave={true}
                        count={true}
                        variables={{
                            index: rowIndex,
                            row: { index: `${rowIndex + 1}` },
                            form,
                            task,
                            entity,
                            config,
                            uploads,
                        }}
                        hashCodeRef={column.hashCodeRef}
                    />
                </>
            )}

            {!column.type && (
                <input
                    defaultValue={column.value}
                    onChange={(ev) =>
                        onChange({
                            column,
                            index,
                            value: ev.currentTarget.value,
                        })
                    }
                    type={"text"}
                    className={"form-control"}
                />
            )}
        </div>
    );
};

const TableEditor: any = ({
    columns,
    data,
    indexCount,
    activeRows,
    onRevert,
    onChange,
    rowIndex,
    isVisible,
    onHide,
    onSave,
    designer,
    schema,
    inventoryItems,
    onCreateInventoryItem,
    perRowColumns = [],
    onPerRowChange,
}) => {
    const filteredColumns = columns
        .map((column, originalIndex) => ({ column, originalIndex }))
        .filter(({ column }) => !isReparaturTitle(column?.title))
        .filter(({ column }) => activeRows.length <= 1 || !isBulkLockedColumn(column));

    const showPerRow = activeRows.length > 1 && perRowColumns.length > 0;
    const contextInventoryItems = useContext(InventoryItemsContext);
    const perRowInventoryItems = useColumnInventoryItems(
        isVisible && showPerRow,
        perRowColumns[0]?.cells[0]?.column?.form?.id || designer?.form?.id,
        contextInventoryItems ?? inventoryItems
    );

    return (
        <Modal size="lg" show={isVisible} backdrop="static" onHide={onHide} keyboard={false}>
            <Modal.Header>
                <Button variant="secondary" onClick={onRevert}>
                    Schließen
                </Button>
                {activeRows.length > 1 && <h3>{activeRows.length} Zeilen bearbeiten</h3>}
                <Button variant="primary" onClick={onSave}>
                    Speichern
                </Button>
            </Modal.Header>
            <Modal.Body>
                {filteredColumns.map(({ column, originalIndex }) => {
                    return (
                        <Row key={originalIndex}>
                            {column.type !== "index" && (
                                <>
                                    <Col className="table-editor-cell">
                                        <strong>{stripTags(column?.title)}</strong>
                                    </Col>
                                    <Col className="table-editor-cell">
                                        <TableColumnEditor
                                            index={originalIndex}
                                            table={data}
                                            rowIndex={rowIndex}
                                            totalIndex={data.length + 1}
                                            column={column}
                                            onChange={onChange}
                                            activeRows={activeRows}
                                            designer={designer}
                                            schema={schema}
                                            inventoryItems={inventoryItems}
                                            onCreateInventoryItem={onCreateInventoryItem}
                                        />
                                    </Col>
                                </>
                            )}
                        </Row>
                    );
                })}

                {showPerRow && (
                    <>
                        <h4 style={{ marginTop: 24 }}>Pro Zeile</h4>
                        {perRowColumns.map(({ rowIndex: perRowIndex, label, cells }) => (
                            <div
                                key={`perrow-${perRowIndex}`}
                                style={{
                                    borderTop: "1px solid #ddd",
                                    paddingTop: 12,
                                    marginTop: 12,
                                }}
                            >
                                <strong style={{ display: "block", marginBottom: 8 }}>
                                    {label}
                                </strong>
                                {cells.map(({ column, columnIndex }) => (
                                    <Row key={`perrow-${perRowIndex}-${columnIndex}`}>
                                        <Col className="table-editor-cell">
                                            <strong>{stripTags(column?.title)}</strong>
                                        </Col>
                                        <Col className="table-editor-cell">
                                            <TableColumnEditor
                                                index={columnIndex}
                                                table={data}
                                                rowIndex={perRowIndex}
                                                totalIndex={data.length + 1}
                                                column={column}
                                                onChange={(change) =>
                                                    onPerRowChange(perRowIndex, change)
                                                }
                                                activeRows={activeRows}
                                                designer={designer}
                                                schema={schema}
                                                inventoryItems={perRowInventoryItems}
                                                onCreateInventoryItem={onCreateInventoryItem}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        ))}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onRevert}>
                    Schließen
                </Button>
                <Button variant="primary" onClick={onSave}>
                    Speichern
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const Table: any = ({
    initialData,
    id,
    columns,
    readonly = false,
    formName,
    form,
    designer,
    schema,
    showSummary: showSummaryProp,
    inventoryItems,
    onCreateInventoryItem,
}) => {
    const [activeRowIndizes, setActiveRowIndizes]: any = useState([]);
    const [editorVisible, setEditorVisibility] = useState(false);
    const [rowIndex, setRowIndex] = useState();
    const [data, setData] = useState(() =>
        hydrateAlignedRows(
            alignTableRows(initialData, toAlignedColumns(columns)).rows,
            toAlignedColumns(columns)
        )
    );

    const [editingRow, setEditingRow] = useState([]);
    const [editingMultiple, setEditingMultiple] = useState([]);
    const [perRowColumns, setPerRowColumns] = useState([]);
    const [perRowEdits, setPerRowEdits] = useState<PerRowEdits>({});

    const [isEditing, setIsEditing] = useState(false);
    const [appendDirection, setAppendDirection] = useState("append");

    const containsServiceColumn =
        columns.some((column) => column?.title === "<p>Service von (Uhrzeit)</p>") &&
        columns.some((column) => column?.title === "<p>Service bis (Uhrzeit)</p>");

    const showSummary = FORMS_WITH_SUMMARY.includes(formName) && containsServiceColumn;
    const hideMangelConfig = schema?.getConfig?.()?.hideMangelLabel;
    const hideMangelLabel = hideMangelConfig === true || hideMangelConfig === "on";

    const toggleActiveRow = (row) => {
        if (activeRowIndizes.includes(row)) {
            setActiveRowIndizes(activeRowIndizes.filter((r) => r !== row));
            return;
        }

        return setActiveRowIndizes([...activeRowIndizes, row]);
    };

    const addRow = (direction = "append") => {
        setIsEditing(false);
        setAppendDirection(direction);
        setEditingRow(
            columns.map((c) => ({
                ...c,
                value: undefined,
                form, // editor-only
            }))
        );

        switch (direction) {
            case "append":
                setRowIndex(data.length as any);
                break;
            case "below":
                setRowIndex(data.length as any);
                break;
            case "above":
                setRowIndex((data.length - 1) as any);
                break;
        }

        setEditorVisibility(true);
    };

    const addRowBelow = () => {
        addRow("below");
    };

    const addRowAbove = () => {
        addRow("above");
    };

    const copyRow = () => {
        setData((data) => {
            const copiedRows = activeRowIndizes.map((index) =>
                buildPersistedRow(data[index], columns)
            );
            return [...data, ...copiedRows];
        });
        setActiveRowIndizes([]);
    };

    const onSave = () => {
        setEditorVisibility(false);
        setData((data) => {
            if (isEditing === false) {
                const newRow = buildPersistedRow(editingRow, columns);

                if (appendDirection === "append") {
                    return [...data, newRow];
                }

                if (activeRowIndizes.length === 1) {
                    const activeRow = activeRowIndizes[0];

                    if (appendDirection === "above") {
                        const dataBefore = data.slice(0, activeRow);
                        const dataAfter = data.slice(activeRow);
                        return [...dataBefore, newRow, ...dataAfter];
                    }

                    if (appendDirection === "below") {
                        const dataBefore = data.slice(0, activeRow + 1);
                        const dataAfter = data.slice(activeRow + 1);
                        return [...dataBefore, newRow, ...dataAfter];
                    }
                }
            }

            if (activeRowIndizes.length > 1) {
                return applyMultiRowEdit(data, activeRowIndizes, editingRow, perRowEdits, columns);
            }

            return data.map((dataRow, index) => {
                if (activeRowIndizes[0] !== index) {
                    return dataRow;
                }

                return buildPersistedRow(editingRow, columns);
            });
        });

        setEditingRow([]);
        setPerRowColumns([]);
        setPerRowEdits({});
        setActiveRowIndizes([]);
    };

    const removeRow = () => {
        setData((data) => {
            const newData = [...data];
            activeRowIndizes.forEach((activeRow: number) => {
                newData[activeRow] = null;
            });

            return newData.filter((n) => !!n);
        });

        setActiveRowIndizes([]);
    };

    const editRow = () => {
        if (activeRowIndizes.length === 1) {
            setRowIndex(activeRowIndizes[0]);
            setEditingRow(
                buildDisplayRow(data[activeRowIndizes[0]], columns).map((c, columnIndex) => {
                    const baseColumn = columns[columnIndex] || {};
                    return {
                        ...baseColumn,
                        ...c,
                        form,
                        field: baseColumn.field || c.field,
                        type: baseColumn.type || c.type,
                        title: baseColumn?.title || c?.title,
                    };
                })
            );
        } else {
            setRowIndex(data.length as any);
            setEditingRow(
                columns.map((c) => {
                    return {
                        ...c,
                        value: undefined,
                        notes: "",
                        form,
                    };
                })
            );
            setPerRowColumns(buildPerRowColumns());
        }
        setPerRowEdits({});
        setIsEditing(true);
        setEditorVisibility(true);
    };

    const buildPerRowColumns = () => {
        return [...activeRowIndizes]
            .sort((a, b) => a - b)
            .map((activeRow) => {
                const displayRow = buildDisplayRow(data[activeRow], columns);
                const summary = displayRow
                    .filter(
                        (cell, columnIndex) =>
                            cell?.type !== "index" &&
                            stripTags(columns[columnIndex]?.title || "").trim() !== "#" &&
                            !isBulkLockedColumn(columns[columnIndex]) &&
                            typeof cell?.value === "string" &&
                            cell.value.trim()
                    )
                    .slice(0, 2)
                    .map((cell) => cell.value.trim());

                return {
                    rowIndex: activeRow,
                    label: [`Zeile ${activeRow + 1}`, summary.join(" / ")]
                        .filter(Boolean)
                        .join(" – "),
                    cells: displayRow
                        .map((cell, columnIndex) => {
                            const baseColumn = columns[columnIndex] || {};
                            return {
                                columnIndex,
                                column: {
                                    ...baseColumn,
                                    ...cell,
                                    form,
                                    field: baseColumn.field || cell?.field,
                                    type: baseColumn.type || cell?.type,
                                    title: baseColumn?.title || cell?.title,
                                },
                            };
                        })
                        .filter(
                            ({ column }) =>
                                isPerRowColumn(column) && !isReparaturTitle(column?.title)
                        ),
                };
            })
            .filter(({ cells }) => cells.length > 0);
    };

    const onPerRowChange = (perRowIndex: number, { index, value, notes }) => {
        setPerRowEdits((edits) => {
            const rowEdits = edits[perRowIndex] || {};
            const nextCell: any = { ...rowEdits[index], value };
            if (notes !== undefined) {
                nextCell.notes = notes;
            }
            return { ...edits, [perRowIndex]: { ...rowEdits, [index]: nextCell } };
        });
    };

    const livePerRowColumns = perRowColumns.map((perRow) => ({
        ...perRow,
        cells: perRow.cells.map(({ columnIndex, column }) => ({
            columnIndex,
            column: { ...column, ...perRowEdits[perRow.rowIndex]?.[columnIndex] },
        })),
    }));

    const onChange = ({ column, index, value, notes }) => {
        setEditingRow((editing) => {
            const next = [...editing];
            const nextColumn = {
                ...next[index],
                value,
            };
            if (notes !== undefined) {
                nextColumn.notes = notes;
            }
            next[index] = nextColumn;
            return next;
        });
        return;
    };

    const revert = () => {
        setEditingRow([]);
        setPerRowColumns([]);
        setPerRowEdits({});
        setEditorVisibility(false);
    };

    return (
        <div>
            <input type={"hidden"} name={id} id={id} defaultValue={JSON.stringify(data)} />

            <TableEditor
                isVisible={editorVisible}
                onHide={() => setEditorVisibility(false)}
                data={data}
                rowIndex={rowIndex}
                columns={editingRow}
                activeRows={activeRowIndizes}
                onSave={onSave}
                onChange={onChange}
                onRevert={revert}
                designer={designer}
                schema={schema}
                inventoryItems={inventoryItems}
                onCreateInventoryItem={onCreateInventoryItem}
                perRowColumns={livePerRowColumns}
                onPerRowChange={onPerRowChange}
            />

            <TablePresenter
                activeRows={activeRowIndizes}
                columns={columns}
                data={data}
                onRowClick={(row) => toggleActiveRow(row)}
                showSummary={showSummary}
                hideMangelLabel={hideMangelLabel}
            />

            {(!readonly || activeRowIndizes.length > 0) && (
                <StickyTableActions>
                    <TableActions
                        editRow={editRow}
                        copyRows={copyRow}
                        removeRow={removeRow}
                        activeRows={activeRowIndizes}
                        addRow={() => {
                            setActiveRowIndizes([]);
                            addRow("append");
                        }}
                        selectAllRows={() => {
                            setActiveRowIndizes(data.map((_, i) => i));
                        }}
                        addRowBelow={addRowBelow}
                        addRowAbove={addRowAbove}
                        readonly={readonly}
                    />
                </StickyTableActions>
            )}
        </div>
    );
};
